import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Input, message, InputNumber, Checkbox } from "antd";
import { all_routes } from "../../../router/all_routes";
import { parseSQLDate, formatToSQLDate } from "../../../utils/dateUtils";
import { GENDER_OPTIONS, CITIES_OPTIONS } from "../../../constants/userConstants";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import { isValidCIN, isValidPhoneNumber } from "utils/validators";
import CommonSelectLite from "components/Common/CommonSelectLite";
import { CONTRACT_TYPE_OPTIONS } from "constants/staffConstants";
import { useAbility } from "config/abilityContext";
import useUserManagement from "../hooks/useUserManagement";
import CommonFormLayout from "components/Common/CommonFormLayout";
import CommonSelect from "components/Common/CommonSelect";
import useRoles from "../hooks/useRoles";
import useReference from "hooks/useReference";



const UserManagerForm = () => {
  const { t } = useTranslation();
  const routes = all_routes;
  const { id: managerId } = useParams<{ id: string }>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [addInfoStaff, setAddInfoStaff] = React.useState<boolean>(false);
  const ability = useAbility();
  const havePermissionStaff = isEdit ? ability.can("update", "Staff") : ability.can("create", "Staff");
  const contract = Form.useWatch(['staff', 'contract'], form) ?? null;
  const translatedGender = GENDER_OPTIONS.map((gender) => ({
    value: gender.value,
    label: t(gender.label),
  }));
  const { handleGetUserById, handleCreate, handleUpdate } = useUserManagement()
  const { jobPositions, fetchJobPositions } = useReference()
  const { roles, fetchRoles } = useRoles();
  const [staffIsExistInEdit, setStaffIsExistInEdit] = React.useState<boolean>(false);

  useEffect(() => {
    fetchRoles();
    fetchJobPositions();

    if (managerId) {
      setIsEdit(true);
      fetchUserManagerData(managerId);
    }
  }, [managerId]);


  const ALLOWED_SYSTEM_SLUGS = useMemo(
    () => new Set(["director", "pedagogy-director"]), // accepts only these two system roles
    []
  );

  // map to CommonSelect options
  const roleOptions = useMemo(
    () =>
      (roles || [])
        .filter(r => (r.isSystem ? ALLOWED_SYSTEM_SLUGS.has(r.slug) : true))
        .map(r => ({
          value: r.id,                 // send roleId
          label: t(r.name),  // i18n key if exists, else name
          slug: r.slug,                // keep slug for legacy 'role'
        })),
    [roles, ALLOWED_SYSTEM_SLUGS, t]
  );

  const jobPositionOptions = useMemo(
    () => jobPositions?.map((jobPosition) => ({
      value: jobPosition.id,
      label: t("job_positions." + jobPosition.code),
    })),
    [jobPositions]
  );

  const fetchUserManagerData = async (id: string) => {
    try {
      const userManagerResponse = await handleGetUserById(id);
      if (!userManagerResponse) return false;
      const initialData = {
        user: {
          firstName: userManagerResponse?.firstName || "",
          lastName: userManagerResponse?.lastName || "",
          email: userManagerResponse?.email || "",
          phoneNumber: userManagerResponse?.phoneNumber || "",
          birthDate: parseSQLDate(userManagerResponse?.birthDate),
          gender: userManagerResponse?.gender || null,
          cityOfBirth: CITIES_OPTIONS.find((city) => city.name === userManagerResponse?.cityOfBirth)?.name || "",
          zipCode: userManagerResponse?.zipCode || "",
          address: userManagerResponse?.address || "",
          city: CITIES_OPTIONS.find((city) => city.name === userManagerResponse?.city)?.name || "",
          roleId: userManagerResponse?.roles?.[0]?.id || null,
          role: userManagerResponse?.roles?.[0]?.slug == "director" ? "director" : "manager",
        },
        staff: userManagerResponse.staff ? {
          contract: userManagerResponse.staff?.contract || "",
          startDate: parseSQLDate(userManagerResponse.staff?.startDate),
          endDate: parseSQLDate(userManagerResponse.staff?.endDate),
          monthlySalary: Number(userManagerResponse.staff?.monthlySalary) || 0,
          cinNumber: userManagerResponse.staff?.cinNumber || null,
          cnssNumber: userManagerResponse.staff?.cnssNumber || null,
          matricule: userManagerResponse.staff?.matricule || null,
          jobPositionId: userManagerResponse.staff?.jobPositionId || null,

        } : null,
      };

      setStaffIsExistInEdit(initialData?.staff !== null);
      form.setFieldsValue(initialData);
      setAddInfoStaff(initialData?.staff !== null);
    } catch (error) {
      console.log("error", error)
      message.error(t("failed_to_load_teacher_data"));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      console.log("values", values)
      const userManagerResponse = {
        ...values,
        user: {
          ...values.user,
          birthDate: values.user.birthDate
            ? formatToSQLDate(values.user.birthDate)
            : null,
        },
        staff: values?.staff ? {
          ...values.staff,
          startDate: values?.staff?.startDate
            ? formatToSQLDate(values.staff.startDate)
            : null,
          endDate: values.staff.endDate
            ? formatToSQLDate(values.staff.endDate)
            : null,

        } : null,
      };
      console.log("userManagerResponse, userManagerResponse", userManagerResponse)
      let response = false;
      if (isEdit) {
        response = await handleUpdate(managerId!, userManagerResponse);
        // message.success(t("teacher_updated_successfully"));
      } else {
        response = await handleCreate(userManagerResponse);
        // message.success(t("teacher_added_successfully"));
      }

      if (response) {
        navigate(routes.userManagement);
      }
    } catch (error) {
      console.log("err", error)
    }
  };

  const sections = [
    {
      title: t("personal_information"),
      content: (
        <div className="row">
          <div className="col-md-4">
            <Form.Item
              label={t("first_name")}
              name={["user", "firstName"]}
              rules={[{ required: true, message: t("validation.firstName_required") }]}
            >
              <Input placeholder={t("enter_first_name")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("last_name")}
              name={["user", "lastName"]}
              rules={[{ required: true, message: t("validation.lastName_required") }]}
            >
              <Input placeholder={t("enter_last_name")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("gender")}
              name={["user", "gender"]}
              rules={[{ required: true, message: t("gender_required") }]}
            >
              <CommonSelect
                options={translatedGender}
                defaultValue={translatedGender.find(
                  (opt) => opt.value === form.getFieldValue(["user", "gender"])
                )}
                onChange={(option) =>
                  form.setFieldsValue({
                    user: {
                      ...form.getFieldsValue(),
                      gender: option?.value ?? "",
                    },
                  })
                }
              />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("email_address")}
              name={["user", "email"]}
              rules={
                [
                  { required: true, message: t("email_required") },
                  { type: "email", message: t("validation.please_enter_valid_email") },
                ]}
            >
              <Input placeholder={t("enter_email")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("phone_number")}
              name={["user", "phoneNumber"]}
              rules={[{ required: true, message: t("validation.phoneNumber_required") }, {
                validator: (_, value) =>
                  value && !isValidPhoneNumber(value)
                    ? Promise.reject(t("validation.phone_number_invalid"))
                    : Promise.resolve(),
              },]}
            >
              <Input placeholder={t("enter_phone_number")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("birth_date")}
              name={["user", "birthDate"]}
              rules={[{ required: true, message: t("validation.birthdate_required") }]}
            >
              <LocalizedDatePicker className="form-control datetimepicker" pastOnly />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("city_of_birth")}
              name={["user", "cityOfBirth"]}
              rules={[{ required: true, message: t("validation.cityOfBirth_required") }]}
            >
              {/* <Input placeholder={t("enter_city_of_birth")} /> */}
              <CommonSelectLite
                options={CITIES_OPTIONS}
                optionValueKey="name"
                optionLabelKey="name"
                value={form.getFieldValue(["user", "cityOfBirth"]) || "casablanca"}
                placeholder={t("enter_city_of_birth")}
                onChange={(value) =>
                  form.setFieldsValue({
                    user: {
                      ...form.getFieldsValue(),
                      cityOfBirth: value,
                    },
                  })
                }
              />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("address")}
              name={["user", "address"]}
              rules={[{ required: true, message: t("validation.address_required") }]}
            >
              <Input placeholder={t("enter_address")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("zip_code")}
              name={["user", "zipCode"]}
              rules={[{ required: true, message: t("validation.zipCode_required") }]}
            >
              <Input placeholder={t("enter_zip_code")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("city")}
              name={["user", "city"]}
              rules={[{ required: true, message: t("validation.city_required") }]}
            >
              {/* <Input placeholder={t("enter_city")} /> */}
              <CommonSelectLite
                options={CITIES_OPTIONS}
                optionValueKey="name"
                optionLabelKey="name"
                placeholder={t("enter_city")}
                value={form.getFieldValue(["user", "city"])}
                onChange={(value) =>
                  form.setFieldsValue({
                    user: {
                      ...form.getFieldsValue(),
                      city: value,
                    },
                  })
                }
              />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item name={["user", "role"]} hidden>
              <Input type="hidden" />
            </Form.Item>

            <Form.Item
              label={t("role")}
              name={["user", "roleId"]}
              rules={[
                { required: true, message: t("validation.please_select_role") },
              ]}
            >
              <CommonSelect
                options={roleOptions}
                defaultValue={roleOptions.find((item) => item?.value == form.getFieldValue(["user", "roleId"]))}
                onChange={(option: any) => {
                  form.setFieldsValue({
                    user: {
                      ...form.getFieldsValue(),
                      roleId: option?.value ?? null,
                      role: (option?.slug == "director" ? "director" : "manager"),
                    },
                  })

                }}
              />
            </Form.Item>
          </div>
        </div>
      ),
    },
    {
      title: t("staff.info_contract"),
      hidden: !havePermissionStaff,
      content: (
        <>
          {(!isEdit || !staffIsExistInEdit) && (
            <div className="row mb-2">
              <Checkbox
                checked={addInfoStaff}
                onChange={(e) =>
                  setAddInfoStaff(e.target.checked)
                }
              >
                {t("staff.add_info_staff")}
              </Checkbox>
            </div>
          )}

          {addInfoStaff && (
            <div className="row">
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.contractType")}
                  name={["staff", "contract"]}
                  rules={[{ required: true, message: t("staff.validation.contractType_required") }]}
                >
                  <CommonSelectLite
                    options={CONTRACT_TYPE_OPTIONS}
                    optionValueKey="value"
                    optionLabelKey="label"
                    placeholder={t("staff.form.contractType")}
                    value={form.getFieldValue("staff.contract")}
                    onChange={(value) =>
                      form.setFieldsValue({
                        staff: {
                          ...form.getFieldsValue().staff,
                          contract: value,
                        },
                      })
                    }
                  />
                </Form.Item>
              </div>

              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.startDate")}
                  name={["staff", "startDate"]}
                  rules={[{ required: true, message: t("staff.validation.startDate_required") }]}
                >
                  <LocalizedDatePicker className="form-control datetimepicker" />
                </Form.Item>
              </div>
              {contract !== "CDI" && (
                <div className="col-md-4">
                  <Form.Item
                    label={t("staff.form.endDate")}
                    name={["staff", "endDate"]}
                    rules={[{ required: true, message: t("staff.validation.endDate_required") },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const startDate = getFieldValue(["staff", "startDate"]);
                        if (!value || !startDate) return Promise.resolve();
                        if (value.isAfter(startDate, "day")) return Promise.resolve();
                        return Promise.reject(
                          new Error(t("staff.validation.endDate_must_be_after_start_date"))
                        );
                      },
                    }),
                    ]}
                  >
                    <LocalizedDatePicker className="form-control datetimepicker" />
                  </Form.Item>
                </div>
              )}
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.monthlySalary")}
                  name={["staff", "monthlySalary"]}
                  rules={[{ required: true, message: t("staff.validation.monthlySalary_required") }]}
                >
                  <InputNumber
                    placeholder={t("staff.placeholders.enter_monthly_salary")}
                    min={0}
                    precision={2}
                    className="d-flex"
                    addonAfter="MAD"
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                  />
                </Form.Item>
              </div>
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.cinNumber")}
                  name={["staff", "cinNumber"]}
                  rules={[{ required: true, message: t("staff.validation.cinNumber_required") }, {
                    validator: (_, value) =>
                      value && !isValidCIN(value)
                        ? Promise.reject(t("staff.validation.cinNumber_invalid"))
                        : Promise.resolve(),
                  }]}
                >
                  <Input
                    placeholder={t("staff.placeholders.enter_cinNumber")}
                  />
                </Form.Item>
              </div>
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.cnssNumber")}
                  name={["staff", "cnssNumber"]}
                >
                  <Input
                    placeholder={t("staff.placeholders.enter_cnssNumber")}
                  />
                </Form.Item>
              </div>
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.matricule")}
                  name={["staff", "matricule"]}
                >
                  <Input
                    placeholder={t("staff.placeholders.enter_matricule")}
                  />
                </Form.Item>
              </div>
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.jobPosition")}
                  name={["staff", "jobPositionId"]}
                  rules={[{ required: true, message: t("staff.validation.jobPosition_required") }]}
                >
                  <CommonSelect
                    options={jobPositionOptions}
                    defaultValue={jobPositionOptions.find(
                      (opt) => opt.value === form.getFieldValue(["staff", "jobPositionId"])
                    )}
                    onChange={(option) =>
                      form.setFieldsValue({
                        staff: {
                          ...form.getFieldsValue(),
                          jobPositionId: option?.value ?? "",
                        },
                      })
                    }
                  />
                </Form.Item>
              </div>
            </div>
          )}
        </>
      ),
    },
  ];

  return <CommonFormLayout title={isEdit ? t("edit_user_management") : t("add_user_management")} form={form} onFinish={handleSubmit} sections={sections} cancelRoute={routes.userManagement} submitText={t("add")} isEdit={isEdit} />;
};

export default UserManagerForm;
